import { getRuntimeMode, isIosNativeAppRuntime, openExternalUrl, type RuntimeMode } from './runtimeCapabilities';

export const ZIAWELLNAV_ACCOUNT_SUPPORT_URL = 'mailto:support@ziawellnav.com?subject=ZiaWellNav%20Company%20Access';
const LOCATION_SUPPORT_URL = 'mailto:support@ziawellnav.com?subject=ZiaWellNav%20Location%20Support';

export type AccountLaunchFeature =
  | 'company_dashboard'
  | 'forms'
  | 'ai'
  | 'fleet'
  | 'timesheets'
  | 'meetings'
  | 'policies'
  | 'training'
  | 'gps'
  | 'cert_vault'
  | 'crew'
  | 'generic';

export interface BuildAccountSupportUrlOptions {
  feature?: AccountLaunchFeature;
  startApp?: string | null;
}

export interface AccountRequiredAccessCopy {
  title: string;
  message: string;
  chromeDemoContext: string;
  actionLabel: string;
  url: string;
  mode: RuntimeMode;
  feature?: AccountLaunchFeature;
}

export type AccountSupportCopy = AccountRequiredAccessCopy;

export interface AccountRequiredPayload {
  error?: unknown;
  message?: unknown;
  hint?: unknown;
  authMode?: unknown;
  actionLabel?: unknown;
  accountSupportUrl?: unknown;
}

export class AccountRequiredError extends Error {
  status?: number;
  payload?: unknown;
  url: string;
  actionLabel: string;
  isAccountRequiredError = true;

  constructor(message: string, options: { status?: number; payload?: unknown; url?: string; actionLabel?: string } = {}) {
    super(message);
    this.name = 'AccountRequiredError';
    this.status = options.status;
    this.payload = options.payload;
    this.url = options.url || ZIAWELLNAV_ACCOUNT_SUPPORT_URL;
    this.actionLabel = options.actionLabel || 'Contact Support';
  }
}

export const IOS_ACCOUNT_REQUIRED_MESSAGE = 'Company database access in the iOS app needs a verified ZiaWellNav account link before protected company records can load. Link this iPhone in iOS App Settings, then try again.';

const ACCOUNT_REQUIRED_PATTERNS = [
  /authentication required/i,
  /user authentication required/i,
  /host web app signed/i,
  /signed account/i,
  /account initdata missing/i,
];

const asPayload = (value: unknown): AccountRequiredPayload | null => {
  if (!value || typeof value !== 'object') return null;
  return value as AccountRequiredPayload;
};

const payloadText = (payload: AccountRequiredPayload): string => [
  payload.error,
  payload.message,
  payload.hint,
].filter((value) => typeof value === 'string').join(' ');

const sourceText = (source: unknown): string => {
  if (typeof source === 'string') return source;
  if (source instanceof Error) return source.message;
  const payload = source instanceof AccountRequiredError
    ? asPayload(source.payload)
    : asPayload(source);
  return payload ? payloadText(payload) : '';
};

const isAuthRequiredPayload = (value: unknown, status?: number): boolean => {
  const payload = asPayload(value);
  if (!payload) return false;
  if (String(payload.authMode || '').endsWith('_signed')) return true;
  const text = payloadText(payload);
  return (status === 401 || status == null) && ACCOUNT_REQUIRED_PATTERNS.some((pattern) => pattern.test(text));
};

export function isIosNativeAccountRequiredPayload(value: unknown, status?: number): boolean {
  return isIosNativeAppRuntime() && isAuthRequiredPayload(value, status);
}

export function isIosNativeAccountRequiredMessage(value: unknown): boolean {
  if (!isIosNativeAppRuntime()) return false;
  const text = sourceText(value);
  return /ios_native_session|native ios|native session|ziawellnav ios account|ios account link|link this iphone|company account link/i.test(text);
}

export function isIosNativeAccountRequiredError(value: unknown): boolean {
  if (!isIosNativeAppRuntime()) return false;
  if (value instanceof Error) return isIosNativeAccountRequiredMessage(value);
  const payload = asPayload(value);
  if (payload && isIosNativeAccountRequiredPayload(payload)) return true;
  return isIosNativeAccountRequiredMessage(value);
}

const sanitizeStartApp = (value?: string | null): string => {
  const trimmed = String(value || '').trim();
  return /^[A-Za-z0-9_-]{1,64}$/.test(trimmed) ? trimmed : '';
};

export function buildAccountSupportUrl(options: BuildAccountSupportUrlOptions = {}): string {
  const startApp = sanitizeStartApp(options.startApp);
  if (!startApp) return ZIAWELLNAV_ACCOUNT_SUPPORT_URL;
  try {
    const url = new URL(ZIAWELLNAV_ACCOUNT_SUPPORT_URL);
    url.searchParams.set('startapp', startApp);
    return url.toString();
  } catch {
    return `${ZIAWELLNAV_ACCOUNT_SUPPORT_URL}?startapp=${encodeURIComponent(startApp)}`;
  }
}

export function inferAccountLaunchFeature(value: unknown): AccountLaunchFeature {
  const text = sourceText(value).toLowerCase();
  if (/gps|geolocation|location api|phone gps/.test(text)) return 'gps';
  if (/ai|rag|knowledge|model|provider|llama|gemma|qwen|grok|openai|ollama/.test(text)) return 'ai';
  if (/meeting|rsvp|check in|check-in/.test(text)) return 'meetings';
  if (/policy|policies|acknowledge/.test(text)) return 'policies';
  if (/training|quiz|course|lesson/.test(text)) return 'training';
  if (/fleet|asset|pm alert|shop request|routing/.test(text)) return 'fleet';
  if (/timesheet|time entr|timer|work timer/.test(text)) return 'timesheets';
  if (/form|jsa|submission|inspection/.test(text)) return 'forms';
  if (/cert|license|licence|vault/.test(text)) return 'cert_vault';
  if (/crew|member|profile|worker|company dashboard|business seat|company record|company/.test(text)) return 'company_dashboard';
  return 'generic';
}

const featureCopy: Record<AccountLaunchFeature, Omit<AccountSupportCopy, 'url' | 'mode' | 'feature'>> = {
  company_dashboard: {
    title: 'Company Account Required',
    message: 'Company records require an organization-managed ZiaWellNav account. Link this iPhone in iOS App Settings or contact support.',
    chromeDemoContext: 'Public navigation stays available. Private company records require administrator assignment.',
    actionLabel: 'Contact Support',
  },
  forms: {
    title: 'Company Account Required',
    message: 'Submit signed company Forms, JSAs, inspections, and worker history after your organization assigns access.',
    chromeDemoContext: 'Public navigation stays available. Private company forms require administrator assignment.',
    actionLabel: 'Contact Support',
  },
  ai: {
    title: 'Company Account Required',
    message: 'AI and private company knowledge require an organization-managed ZiaWellNav account.',
    chromeDemoContext: 'Public tools stay available. Private company knowledge requires administrator assignment.',
    actionLabel: 'Contact Support',
  },
  fleet: {
    title: 'Company Account Required',
    message: 'Fleet management requires organization access for company assets, routing history, shop requests, and PM records.',
    chromeDemoContext: 'Public navigation stays available. Fleet records require administrator assignment.',
    actionLabel: 'Contact Support',
  },
  timesheets: {
    title: 'Company Account Required',
    message: 'Timesheets, work timers, and signed worker time history require organization access.',
    chromeDemoContext: 'Public navigation stays available. Timesheets require administrator assignment.',
    actionLabel: 'Contact Support',
  },
  meetings: {
    title: 'Company Account Required',
    message: 'Meeting updates, RSVP, check-in, and attendance records require organization access.',
    chromeDemoContext: 'Public tools stay available. Company meetings require administrator assignment.',
    actionLabel: 'Contact Support',
  },
  policies: {
    title: 'Company Account Required',
    message: 'Policy review, signatures, issue tracking, and employee acknowledgement records require organization access.',
    chromeDemoContext: 'Public tools stay available. Company policies require administrator assignment.',
    actionLabel: 'Contact Support',
  },
  training: {
    title: 'Company Account Required',
    message: 'Assigned training, quizzes, acknowledgements, and completion records require organization access.',
    chromeDemoContext: 'Public navigation stays available. Company training requires administrator assignment.',
    actionLabel: 'Contact Support',
  },
  gps: {
    title: 'Location Support',
    message: 'This device or browser did not provide GPS. Use the native iOS app with Location Services enabled, or contact support if location still fails.',
    chromeDemoContext: 'ZiaWellNav can still search wells and facilities without GPS; live navigation needs Location Services.',
    actionLabel: 'Contact Support',
  },
  cert_vault: {
    title: 'Company Account Required',
    message: 'Certificate and license sync requires organization access for private worker documents.',
    chromeDemoContext: 'Local document capture remains available where enabled. Company sync requires administrator assignment.',
    actionLabel: 'Contact Support',
  },
  crew: {
    title: 'Company Account Required',
    message: 'Linked crew, profile, and worker records require organization access.',
    chromeDemoContext: 'Public navigation stays available. Crew records require administrator assignment.',
    actionLabel: 'Contact Support',
  },
  generic: {
    title: 'Company Account Required',
    message: 'This feature requires an organization-managed ZiaWellNav account. Link this iPhone in iOS App Settings or contact support.',
    chromeDemoContext: 'Public navigation stays available. Private company workflows require administrator assignment.',
    actionLabel: 'Contact Support',
  },
};

const iosNativeCopy: Omit<AccountSupportCopy, 'mode' | 'feature'> = {
  title: 'Continue in ZiaWellNav',
  message: 'Protected records need an active ZiaWellNav account and Apple purchase. Restore purchases or link this iPhone in iOS App Settings, then try again.',
  chromeDemoContext: '',
  actionLabel: '',
  url: '',
};

export function formatAccountSupportCopy(
  input?: AccountLaunchFeature | (BuildAccountSupportUrlOptions & { reason?: unknown })
): AccountSupportCopy {
  const feature = typeof input === 'string'
    ? input
    : input?.feature || inferAccountLaunchFeature(input?.reason);
  if (isIosNativeAppRuntime()) {
    return {
      ...iosNativeCopy,
      mode: getRuntimeMode(),
      feature,
    };
  }
  const copy = featureCopy[feature] || featureCopy.generic;
  return {
    ...copy,
    url: feature === 'gps'
      ? LOCATION_SUPPORT_URL
      : buildAccountSupportUrl(typeof input === 'string' ? { feature } : { feature, startApp: input?.startApp }),
    mode: getRuntimeMode(),
    feature,
  };
}

export function isAccountRequiredPayload(value: unknown, status?: number): boolean {
  if (isIosNativeAppRuntime()) return false;
  return isAuthRequiredPayload(value, status);
}

export function isAccountRequiredMessage(value: unknown): boolean {
  if (isIosNativeAppRuntime()) return false;

  if (typeof value !== 'string') return false;
  return ACCOUNT_REQUIRED_PATTERNS.some((pattern) => pattern.test(value));
}

export function isAccountRequiredError(value: unknown): value is AccountRequiredError {
  if (isIosNativeAppRuntime()) return false;
  if (value instanceof AccountRequiredError) return true;
  if (!value || typeof value !== 'object') return false;
  const maybeError = value as { isAccountRequiredError?: unknown; message?: unknown; payload?: unknown; status?: unknown };
  if (maybeError.isAccountRequiredError === true) return true;
  if (isAccountRequiredPayload(maybeError.payload, typeof maybeError.status === 'number' ? maybeError.status : undefined)) return true;
  return isAccountRequiredMessage(maybeError.message);
}

export function formatAccountRequiredAccess(source?: unknown, feature?: AccountLaunchFeature): AccountRequiredAccessCopy {
  if (isIosNativeAppRuntime()) {
    return formatAccountSupportCopy({ feature: feature || inferAccountLaunchFeature(source), reason: source });
  }

  const payload = source instanceof AccountRequiredError
    ? asPayload(source.payload)
    : asPayload(source);
  const contextual = formatAccountSupportCopy({ feature: feature || inferAccountLaunchFeature(source), reason: source });
  const actionLabel = String(payload?.actionLabel || (source instanceof AccountRequiredError ? source.actionLabel : '') || contextual.actionLabel);
  const url = String(payload?.accountSupportUrl || (source instanceof AccountRequiredError ? source.url : '') || contextual.url);
  return {
    ...contextual,
    actionLabel,
    url,
  };
}

export function toAccountRequiredError(payload: unknown, status?: number, feature?: AccountLaunchFeature): AccountRequiredError {
  const copy = formatAccountRequiredAccess(payload, feature);
  return new AccountRequiredError(copy.message, {
    status,
    payload,
    url: copy.url,
    actionLabel: copy.actionLabel,
  });
}

export function throwAccountAwareError(payload: unknown, status: number, fallbackMessage: string): never {
  if (isIosNativeAccountRequiredPayload(payload, status)) {
    throw new Error(IOS_ACCOUNT_REQUIRED_MESSAGE);
  }
  if (isAccountRequiredPayload(payload, status)) {
    throw toAccountRequiredError(payload, status, inferAccountLaunchFeature(fallbackMessage));
  }
  const typed = asPayload(payload);
  const message = typeof typed?.message === 'string'
    ? typed.message
    : typeof typed?.error === 'string'
      ? typed.error
      : fallbackMessage;
  throw new Error(message);
}

export function openZiaWellNavAccountSupport(url = ZIAWELLNAV_ACCOUNT_SUPPORT_URL) {
  return openExternalUrl(url, { tryHostBridge: false });
}
